import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  clearLeadClaim,
  disableLeadEngine,
  escalateAdminLead,
  getAdminLead,
  rearmLead,
} from "@/lib/api";
import {
  Button,
  Card,
  type ConfirmOptions,
  ErrorBanner,
  Field,
  PageContainer,
  TextInput,
  toErrorMessage,
  useConfirm,
} from "@/components/Forms";
import { ArrowLeftIcon } from "@/components/Icons";

/**
 * Admin lead detail — read-only full dump of every field on the row plus
 * the four recovery actions: rearm (force NOW), clear-claim (release
 * stuck claim), escalate (force ESCALATED_HUMAN + engine off), and
 * disable-engine (engine off, preserve state).
 */
export default function AdminLeadDetailPage() {
  const { tenant, id } = useParams<{ tenant: string; id: string }>();
  const { t } = useTranslation();
  const [lead, setLead] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadAt, setReloadAt] = useState(0);

  useEffect(() => {
    if (!tenant || !id) return;
    let cancelled = false;
    setError(null);
    getAdminLead(tenant, id)
      .then((r) => !cancelled && setLead(r))
      .catch((e) => !cancelled && setError(toErrorMessage(e, t("leadDetail.loadError"))));
    return () => {
      cancelled = true;
    };
  }, [tenant, id, reloadAt, t]);

  if (error) {
    return (
      <PageContainer title={t("leadDetail.title")} subtitle={t("leads.subtitle")}>
        <ErrorBanner message={error} />
        <Link to="/leads" className="text-sm text-accent hover:underline">
          {t("leadDetail.back")}
        </Link>
      </PageContainer>
    );
  }
  if (!lead || !tenant || !id) {
    return (
      <PageContainer title={t("leadDetail.title")} subtitle={t("leads.subtitle")}>
        <p className="text-sm text-muted-foreground">{t("forms.loading")}</p>
      </PageContainer>
    );
  }

  const name = (lead.name as string | null) ?? "—";

  return (
    <PageContainer
      title={name}
      subtitle={`${t("leadDetail.title")} · ${tenant} · ${id.slice(0, 8)}…`}
      actions={
        <Link
          to="/leads"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeftIcon size={14} /> {t("leadDetail.back")}
        </Link>
      }
    >
      <div className="grid lg:grid-cols-[1fr_18rem] gap-3">
        <Card title={t("leadDetail.factTitle")}>
          <pre className="text-xs font-mono overflow-x-auto max-h-[60vh]">
            {JSON.stringify(lead, null, 2)}
          </pre>
        </Card>

        <ActionPanel
          tenant={tenant}
          leadId={id}
          onChanged={() => setReloadAt((n) => n + 1)}
        />
      </div>
    </PageContainer>
  );
}

function ActionPanel({
  tenant,
  leadId,
  onChanged,
}: {
  tenant: string;
  leadId: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const [actor, setActor] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState<null | "rearm" | "clear" | "escalate" | "disable">(null);
  const [err, setErr] = useState<string | null>(null);
  const confirm = useConfirm();

  async function run(
    label: typeof busy,
    fn: () => Promise<unknown>,
    confirmOpts: ConfirmOptions,
  ) {
    if (!(await confirm(confirmOpts))) return;
    setBusy(label);
    setErr(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(toErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card title={t("leadDetail.actionsTitle")}>
      <ErrorBanner message={err} />
      <div className="space-y-3">
        <Field label={t("leadDetail.fieldActor")} hint={t("leadDetail.fieldActorHint")}>
          <TextInput
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder={t("leadDetail.fieldActorPlaceholder")}
          />
        </Field>
        <Field label={t("leadDetail.fieldReason")}>
          <TextInput
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("leadDetail.fieldReasonPlaceholder")}
          />
        </Field>

        <div className="grid gap-2">
          <Button
            onClick={() =>
              run(
                "rearm",
                () => rearmLead(tenant, leadId, actor || undefined),
                {
                  title: t("leadDetail.rearmConfirmTitle"),
                  body: t("leadDetail.rearmConfirmBody"),
                  confirmLabel: t("leadDetail.rearmConfirmLabel"),
                },
              )
            }
            disabled={busy !== null}
          >
            {busy === "rearm" ? t("leadDetail.rearming") : t("leadDetail.rearm")}
          </Button>
          <Button
            onClick={() =>
              run(
                "clear",
                () => clearLeadClaim(tenant, leadId, actor || undefined),
                {
                  title: t("leadDetail.clearConfirmTitle"),
                  body: t("leadDetail.clearConfirmBody"),
                  confirmLabel: t("leadDetail.clearConfirmLabel"),
                },
              )
            }
            disabled={busy !== null}
          >
            {busy === "clear" ? t("leadDetail.clearing") : t("leadDetail.clear")}
          </Button>
          <Button
            variant="destructive"
            onClick={() =>
              run(
                "escalate",
                () => escalateAdminLead(tenant, leadId, actor || undefined, reason || undefined),
                {
                  title: t("leadDetail.escalateConfirmTitle"),
                  body: t("leadDetail.escalateConfirmBody"),
                  confirmLabel: t("leadDetail.escalateConfirmLabel"),
                  destructive: true,
                },
              )
            }
            disabled={busy !== null}
          >
            {busy === "escalate" ? t("leadDetail.escalating") : t("leadDetail.escalate")}
          </Button>
          <Button
            variant="destructive"
            onClick={() =>
              run(
                "disable",
                () => disableLeadEngine(tenant, leadId, actor || undefined, reason || undefined),
                {
                  title: t("leadDetail.disableConfirmTitle"),
                  body: t("leadDetail.disableConfirmBody"),
                  confirmLabel: t("leadDetail.disableConfirmLabel"),
                  destructive: true,
                },
              )
            }
            disabled={busy !== null}
          >
            {busy === "disable" ? t("leadDetail.disabling") : t("leadDetail.disable")}
          </Button>
        </div>
      </div>
    </Card>
  );
}
